import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { Header } from '@/components/Header';
import { getTodayNews, getOlderNews, getNewsDates } from '@/services/newsService';
import type { CategoryKey, NewsItem, LangKey } from '@/types';

import { Spinner, Title2 } from '@fluentui/react-components';

import { NewsList } from '@/components/NewsList';
import { useUserPrefs } from '@/context/UserPrefsContext';
import { uiText } from '@/i18n/locales';
import { categoryLabels } from '@/i18n/categoryLabels';
import { Footer } from '@/components/Footer';
import { hitAndGetCount, getCount } from '@/services/visitCounter';
import { AdBanner } from '@/components/AdBanner';
import { baseTabs } from '@/components/CategoryTabs';
import {  HeaderRef } from '@/components/Header';


export const CategoryPage: React.FC = () => {
  const { categoryName, routineKey: cityRoutineKey } =
    useParams<{ categoryName?: string; routineKey?: string }>();
const headerRef = useRef<HeaderRef>(null);
  const routineKey = cityRoutineKey ?? categoryName;
  const location = useLocation();
  const isCityRoute = location.pathname.startsWith('/city');

  const { prefs } = useUserPrefs();
  const lang = prefs.language as LangKey;
  const t = uiText[lang] ?? uiText.en;

  const category = useMemo<CategoryKey>(() => {
    if (isCityRoute && routineKey) return routineKey as CategoryKey;
    const path = location.pathname.replace('/', '');
    const tab = baseTabs.find(t => t.path === path);
    if (tab) return tab.key as CategoryKey;
    return '25';
  }, [location.pathname, isCityRoute, routineKey]);

  const [items, setItems] = useState<NewsItem[]>([]);
  const [keys, setKeys] = useState<string[]>([]);
  const [dateIndex, setDateIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [visitCount, setVisitCount] = useState<number | null>(null);

  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const todayKey = new Date().toISOString().slice(0, 10);
const handleLoginOpen = () => {
  headerRef.current?.openLogin();
};

  /* ---------- Visit Counter ---------- */
  useEffect(() => {
    if (!routineKey) return;

    const sessionKey = `visited_${routineKey}`;

    if (sessionStorage.getItem(sessionKey)) {
      getCount(routineKey).then(setVisitCount);
      return;
    }

    const hitCounter = async () => {
      const count = await hitAndGetCount(routineKey);
      setVisitCount(count);
      sessionStorage.setItem(sessionKey, '1');
    };

    hitCounter();
  }, [routineKey]);

  /* ---------- Initial Fetch ---------- */
  useEffect(() => {
    if (isCityRoute && !prefs.city) return;

    let cancelled = false;
      setItems([]);
  setKeys([]);
  setDateIndex(0);
  setLoading(true);

    (async () => {
      try {
        const todayItems = await getTodayNews({
          category,
          language: lang,
        });

        if (cancelled) return;

        setItems(todayItems);

        // console.log('category', category, 'language', lang);
        let dateKeys = await getNewsDates({
          category,
          language: lang,
        });

        if (todayItems.length > 0 && !dateKeys.includes(todayKey)) {
          dateKeys.unshift(todayKey);
        }

        setKeys(dateKeys);
        // console.log('Initial fetch dateKeys:', dateKeys);
        setDateIndex(todayItems.length > 0 ? 0 : -1);


      } catch (err) {
        console.error('CategoryPage fetch error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [category, lang, isCityRoute, prefs.city, routineKey]);

  /* ---------- Lazy Load Older ---------- */
  const loadMore = async () => {
    if (loadingMore) return;

    const nextIndex = dateIndex + 1;
    if (nextIndex >= keys.length) return;

    const dateKey = keys[nextIndex];

    setLoadingMore(true);

    try {
      const older = await getOlderNews({
        category,
        language: lang,
        dateKey,
      });

      setItems(prev => [...prev, ...older]);
      setDateIndex(nextIndex);

    } catch (err) {
      console.error('loadMore error', err);
    } finally {
      setLoadingMore(false);
    }
  };

  /* ---------- Infinite Scroll Observer ---------- */
  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      {
        root: null,
        rootMargin: '300px',
        threshold: 0,
      }
    );

    observer.observe(el);

    return () => observer.disconnect();
  }, [dateIndex, keys, loadingMore]);

  /* ---------- Title ---------- */
  const title = useMemo(() => {
    if (isCityRoute && !routineKey && prefs.city) return prefs.city;

    if (isCityRoute && routineKey) {
      const label =
        categoryLabels[lang]?.[routineKey as CategoryKey] ??
        categoryLabels.en[routineKey as CategoryKey] ??
        routineKey;

      return prefs.city ? `${prefs.city} · ${label}` : label;
    }

    return (
      categoryLabels[lang]?.[category] ??
      categoryLabels.en[category] ??
      category
    );
  }, [isCityRoute, routineKey, prefs.city, category, lang]);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
      padding: 12,
      maxWidth: 1200,
      margin: '0 auto'
    }}>
  

<Header ref={headerRef} /> 

    

      <Title2>{title}</Title2>

      {loading ? (
        <Spinner label={t.loading ?? 'Loading...'} />
      ) : (
        <>
          <NewsList
            items={items}
            simple={prefs.viewMode === 'simple'}
            routineKey={category}  onLoginClick={handleLoginOpen}
          />

          {dateIndex < keys.length - 1 && (
            <div
              ref={loadMoreRef}
              style={{
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {loadingMore && <Spinner size="small" />}
            </div>
          )}
        </>
      )}

      <Footer visitCount={visitCount} />
    </div>
  );
};
